// Data types
// Dependencies
import React, { RefObject } from 'react';
import { connect } from 'react-redux';

// Components
import { Deck as DeckGl, FlyToInterpolator } from '@deck.gl/core';
import { GeoJsonLayer } from '@deck.gl/layers';
import BaseMap from 'components/baseClass/MapBase';
import {
    LAYERS,
    PLAYBACK_SPEED,
    TAB_IDEL_TIMEOUT,
    TOOLTIP_OFFSET,
    TRACK_EVENT_COLOR,
    TRACK_UNUSED_TABS,
} from 'data/static';
import { bindActionCreators, Dispatch } from 'redux';
import {
    setErrorHandlerAction,
    SetErrorHandlerAction,
    setSystemLoadingAction,
    SetSystemLoadingAction,
    ShowMessageAction,
    showMessageAction,
} from 'store/actions/errorsActions';
import { SetLayoutAction, setLayoutAction } from 'store/actions/layoutActions';
import { setMapAction, SetMapAction, SetMapControlsAction, setMapControlsAction } from 'store/actions/mapActions';
import {
    BackgroundType,
    CoordinateArray,
    DeckGlInstance,
    DeckGLLayer,
    LayerType,
    Layout,
    MapProps,
    OpenModal,
    PilotagePlanSettingType,
    PlanType,
    PortType,
    RGBAColor,
    RootState,
    SwitchType,
    ToolTipType,
    TrackType,
    TransitType,
    VesselCategoryType,
} from 'types';
import { GoToVesselLocation, JumpToTimeInterface, MainControlsInterface, MenuControlsInterface } from 'types/functions';
import { GeoJSONFeature, GeoJSONFeatureCollection } from 'types/geojson';
import { hexToRgb } from 'utils/colors';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { getVesselDisplayFeature } from 'utils/drawing';
import { checkDifferent, checkLayoutDifferent, clone, getId } from 'utils/formHandlers';
import geometry from 'utils/geometry';
import {
    filterBySection,
    getLayerProvider,
    getPortCoordinate,
    getScreenCoordinates,
    updateFeatureProperties,
} from 'utils/layers';
import { findBestData, findBestPosition } from 'utils/migrations';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';

import Attribution from './Attribution';
import ChangeLayer from './ChangeLayer';
import Controls from './Controls';
import MeasurePosition from './MeasurePosition';
import MousePosition from './MousePosition';
import StaticToolTip from './StaticToolTip';
import TimeControl from './TimeControl';
import ToolTip from './ToolTip';
import updatePageTitle from 'utils/document';

interface PlaybackComponentProps {
    layout: Layout;
    active: boolean;

    port: PortType;

    //unused props
    outlineInterval: number;
    switchs: SwitchType;
    layers: LayerType;
    setMapControlsAction: SetMapControlsAction;
    setMapAction: SetMapAction;
    openModelFunction: OpenModal;
    setLayoutAction: SetLayoutAction;
    vesselCategories: VesselCategoryType[];
    //unused props
    setErrorHandlerAction: SetErrorHandlerAction;

    //unused props
    setSystemLoadingAction: SetSystemLoadingAction;

    showMessageAction: ShowMessageAction;
    pilotagePlans: PlanType[];
    pilotagePlanSettings: PilotagePlanSettingType;
    dateTimeFormat: DateTimeFormat;

    //unused props
    mainControls: MainControlsInterface;
    menuControls: MenuControlsInterface;
}

interface PlaybackComponentState {
    layout: Layout;
    active: boolean;

    uuid: string;
    zoom: number;

    coordinate: CoordinateArray | null;
    hoverMeasureData: any | null;
    backgrounds: BackgroundType[];
    mapProps?: MapProps;
    switchs: SwitchType;
    map?: DeckGlInstance;
    tooltips: ToolTipType[];
    drawingFeatures: GeoJSONFeatureCollection;
    pilotagePlans: PlanType[];
    pilotagePlanSettings: PilotagePlanSettingType;
    mode: string;
    //unused state
    refreshFeatures: string;

    layers: LayerType;
    outlineFeatures: TrackType[];
    trackFeatures: TrackType[];
    currentTime?: DateTimeFormat;
    startTime?: DateTimeFormat;
    endTime?: DateTimeFormat;
    speed: number;
    play: boolean;
    clickMatrix: any[];
    //unused state
    drawingMode: string;
    analysisVariables: object;
}

class Playback extends BaseMap<PlaybackComponentProps, PlaybackComponentState> {
    private canvasRef: RefObject<HTMLCanvasElement>;
    private timer: ReturnType<typeof setInterval> | undefined = undefined;
    private timeoutId: NodeJS.Timeout | null = null;

    constructor(props: PlaybackComponentProps) {
        super(props);
        this.state = {
            layout: clone(props.layout),
            drawingMode: 'pointer',
            mode: 'playback',
            uuid: '',
            active: true,
            zoom: 10,
            coordinate: null,
            hoverMeasureData: null,
            backgrounds: [],
            drawingFeatures: {
                type: 'FeatureCollection',
                features: [],
            },
            tooltips: [],
            switchs: clone(props.switchs),
            pilotagePlans: [],
            pilotagePlanSettings: {},
            refreshFeatures: getId(),
            layers: props.layers,
            outlineFeatures: [],
            trackFeatures: [],
            speed: PLAYBACK_SPEED,
            play: false,
            clickMatrix: [],
            analysisVariables: props.port.meta_data.analysisVariables,
        };

        // Initialize the ref in the constructor
        this.canvasRef = React.createRef();
        //@ts-ignore
        this.geometry = new geometry(props.port?.projection, 'EPSG:4326');
    }

    static getDerivedStateFromProps(props: PlaybackComponentProps, state: PlaybackComponentState) {
        //VERY important code to keep stop COMM update if not active
        if (!props.layout.sections?.playback.state) {
            if (props.active) {
                updatePageTitle(
                    `${translate('Replay')} ${state.currentTime?.formatDateTimeToString('dd MMM') || ''} ${
                        props.layout.sections.replay.data.transit.vessel.name || ''
                    } · ${props.port.name}`
                );
            }
            return {
                active: props.active,
                layout: clone(props.layout),
                pilotagePlans: props.pilotagePlans,
                pilotagePlanSettings: props.pilotagePlanSettings,
            };
        }

        if (
            props.active !== state.active ||
            checkDifferent(props.switchs, state.switchs, true) ||
            checkDifferent(props.layers, state.layers) ||
            checkLayoutDifferent(props.layout, state.layout, ['sections.playback']) ||
            checkDifferent(props.pilotagePlanSettings, state.pilotagePlanSettings, true) ||
            checkDifferent(props.pilotagePlans, state.pilotagePlans, true)
        ) {
            if (props.active) {
                updatePageTitle(
                    `${translate('Replay')} ${state.currentTime?.formatDateTimeToString('dd MMM') || ''} ${
                        props.layout.sections.replay.data.transit.vessel.name || ''
                    } · ${props.port.name}`
                );
            }
            return {
                active: props.active,
                layout: clone(props.layout),
                switchs: clone(props.switchs),
                layers: clone(props.layers),
                pilotagePlans: props.pilotagePlans,
                pilotagePlanSettings: props.pilotagePlanSettings,
            };
        }

        return null;
    }

    componentDidMount() {
        const { latitude, longitude, zoom } = getPortCoordinate(this.props.port);
        this.setState(
            {
                uuid: getId(),
                zoom,
                coordinate: [latitude, longitude],
                backgrounds: this.getPortBackgrounds('playback'),
            },
            this.createMap
        );
    }

    componentWillUnmount(): void {
        this.props.setMapControlsAction({
            playback: null,
        });
        this.props.setMapAction({
            playback: null,
        });
    }

    updateVesselsOulines = () => {
        console.log('not used');
    };

    closePlaybackMode = () => {
        this.props.setLayoutAction([
            { action: 'UPDATE_SECTION', data: { name: 'replay', state: false, display: true, disable: true } },
            { action: 'UPDATE_SECTION', data: { name: 'time', state: false, display: true, disable: true } },
            {
                action: 'DEFAULT',
                data: {
                    playbackId: '',
                },
            },
            {
                action: 'UPDATE_MENU',
                data: {
                    name: 'playbackMenu',
                    section: 'playback',
                    state: false,
                },
            },
            {
                action: 'UPDATE_DATA',
                data: {
                    section: 'playback',
                    data: {},
                },
            },
        ]);
    };

    async componentDidUpdate(
        props: Readonly<PlaybackComponentProps>,
        state: Readonly<PlaybackComponentState>
    ): Promise<Promise<void>> {
        const previousZoom = state.mapProps?.viewState?.zoom;
        const currentZoom = this.state.mapProps?.viewState?.zoom;

        if (previousZoom !== currentZoom) {
            this.renderOutline();
        }

        // Check if state.switches has changed
        if (state.active !== this.state.active && TRACK_UNUSED_TABS) {
            if (this.state.active) {
                //@ts-ignore
                clearTimeout(this.timeoutId);
                this.timeoutId = null;
            } else {
                this.timeoutId = setTimeout(() => {
                    this.props.openModelFunction(
                        'replayModeTimeOutModel',
                        0,
                        'medium',
                        {},
                        {
                            onCancel: () => {
                                this.props.openModelFunction('replayModeTimeOutModel');
                            },
                            onConfirm: () => this.closePlaybackMode(),
                        },
                        true
                    );
                    this.timeoutId = null;
                }, TAB_IDEL_TIMEOUT);
            }
        }

        if (
            Object.values(this.state.layout.sections.playback.tools).every((tool) => tool.selected === false) &&
            this.state.drawingMode !== 'pointer'
        ) {
            this.setDrawingMode('pointer');
        }
        if (
            checkDifferent(
                filterBySection(state.switchs, 'analysisAreas'),
                filterBySection(this.state.switchs, 'analysisAreas'),
                true
            ) &&
            this.state.map
        ) {
            this.updatePortAreas();
        }

        //TODO set update for each switch only
        if (
            checkDifferent(
                filterBySection(state.switchs, 'dataDisplay'),
                filterBySection(this.state.switchs, 'dataDisplay'),
                true
            ) &&
            this.state.map
        ) {
            // Execute the updates sequentially
            window?.log('INFO : map feature update requested');
            this.renderPoints()
                .then(() => this.renderEvents())
                .then(() => this.renderOutline())
                .catch((error) => console.error('Error updating features:', error));
        }

        if (checkDifferent(state.drawingFeatures, this.state.drawingFeatures, true) && this.state.map) {
            await this.createMeasureTooltip();
            await this.createLabelTooltip();
            await this.createCoordinateTooltip();
            this.createEditlayer();
        }

        // Check if pilotagePlanSettings or pilotagePlanSettings has changed
        if (
            checkDifferent(state.pilotagePlanSettings, this.state.pilotagePlanSettings, true) ||
            (checkDifferent(state.pilotagePlans, this.state.pilotagePlans, true) && this.state.map)
        ) {
            this.updatePilotagePlans(state.pilotagePlans);
        }
    }

    startTimer = () => {
        if (this.state.play) {
            window?.log('INFO : Already playing');
            return;
        }

        this.setState(
            {
                play: true,
            },
            () => {
                window?.log('INFO : Start play');

                let updateCounter = 0; // Counter to track when to call getMetOceanData

                this.timer = setInterval(() => {
                    if (this.state.currentTime?.isAfter(this.state.endTime?.date)) {
                        this.props.showMessageAction({
                            messageType: 'INFO',
                            message: translate('Playback completed.'),
                        });

                        this.stopTimer();
                        return;
                    }

                    this.setState(
                        (prevState) => ({
                            currentTime: prevState.currentTime?.clone().plus({ second: 1 * this.state.speed }), // Increment time
                        }),
                        () => {
                            updateCounter++;

                            if (updateCounter % 10 === 0) {
                                this.props.menuControls.playback?.getMetOceanByTime(this.state.currentTime);
                            }

                            this.renderOutline();
                        }
                    );
                }, 1000);
            }
        );
    };

    stopTimer = () => {
        if (!this.state.play) {
            window?.log('INFO : Aready stoped');
            return;
        }

        this.setState(
            {
                play: false,
            },
            () => {
                window?.log('INFO : Stop play');
                clearInterval(this.timer);
                this.timer = undefined;
            }
        );
    };

    setViewPort = (latitude: number, longitude: number, zoom?: number) => {
        console.log(latitude, longitude);
        this.setState(
            {
                mapProps: {
                    ...this.state.mapProps,
                    //@ts-ignore
                    viewState: {
                        ...this.state.mapProps?.viewState,
                        longitude,
                        latitude,
                        zoom: zoom || this.state.mapProps?.viewState?.zoom,
                        transitionDuration: 50, // Smooth transition duration
                        transitionInterpolator: new FlyToInterpolator(), // Smooth fly-to effect}
                    },
                },
            },
            () => {
                this.state.map?.setProps({ viewState: this.state.mapProps?.viewState });
            }
        );
    };

    updateTracks = (
        tracks: TrackType[],
        selectedTime: DateTimeFormat | null,
        startTime: DateTimeFormat,
        endTime: DateTimeFormat,
        transit: TransitType | undefined
    ) => {
        window?.log('INFO : Map feature replace requested');
        console?.log('INFO : Map feature replace requested');

        this.setState(
            {
                currentTime: selectedTime ? selectedTime : this.state.currentTime,
                startTime: startTime,
                endTime: endTime,
                // tooltips: [],
                outlineFeatures: tracks.filter((track) => {
                    return track.options.display;
                }),

                trackFeatures: tracks.filter((track) => {
                    return track.options.drawTrack;
                }),
            },
            () => {
                this.renderTracks()
                    .then(() => this.renderPoints())
                    .then(() => this.renderEvents())
                    .then(() => this.renderOutline())
                    .catch((error) => console.error('Error updating features:', error));
            }
        );
    };

    goToVesselLocation: GoToVesselLocation = (uuid: string, showTooltip = true, zoom = 16) => {
        const { outlineFeatures, currentTime, map, tooltips } = this.state;

        outlineFeatures
            .filter((x) => x.uuid === uuid)
            .forEach((outlineFeature) => {
                let position = outlineFeature.data.rawAIS
                    ?.slice() // copy array
                    .reverse()
                    //@ts-ignore
                    .find((item) => item.valid_time_utc.toTimestamp() <= this.state.currentTime?.toTimestamp());

                if (!position) {
                    position = outlineFeature.data.rawAIS[outlineFeature.data.rawAIS.length - 1];
                }

                // Transform UTM coordinates to map coordinates
                const [longitude, latitude] = this.geometry.transformer([position.east_utm, position.north_utm]);
                //@ts-ignore
                const currentViewState = map?.props?.viewState || {}; // Fallback to an empty object if undefined
                const { x, y } = getScreenCoordinates(longitude, latitude, currentViewState);
                // Retrieve current viewState from the map

                const tooltip = {
                    static: false,
                    uuid: getId(),
                    type: LAYERS.playbackVesselOutline.name,
                    event: 'onClick',
                    bind: false,
                    longitude: longitude,
                    latitude: latitude,
                    east_utm: position.east_utm,
                    north_utm: position.north_utm,
                    x: x + TOOLTIP_OFFSET,
                    y: y + TOOLTIP_OFFSET,
                    xOffset: 0,
                    yOffset: 0,
                    dataIndex: {
                        type: 'rawAIS',
                        navigational_status_id: position.navigational_status_id,
                        index: null,
                        time: position?.valid_time_utc,
                    },
                    data: {
                        ...outlineFeature.vessel,
                        parent: outlineFeature.uuid,
                        layerGroup: LAYERS.playbackVesselOutline.group,
                        drawIndex: LAYERS.playbackVesselOutline.drawIndex,
                        clickIndex: LAYERS.playbackVesselOutline.clickIndex,
                        layer: LAYERS.playbackVesselOutline.name,
                    },
                    visible: true,
                };
                // Update the map's view state with the new location\

                const newTooltips = showTooltip
                    ? [tooltip, ...tooltips.filter((x) => x.bind)]
                    : tooltips.filter((x) => x.bind);

                this.setState(
                    {
                        tooltips: newTooltips,
                        mapProps: {
                            ...this.state.mapProps,
                            //@ts-ignore
                            viewState: {
                                ...this.state.mapProps?.viewState,
                                longitude,
                                latitude,
                                zoom: zoom, // Optional: Set a desired zoom level
                                transitionDuration: 50, // Smooth transition duration
                                transitionInterpolator: new FlyToInterpolator(), // Smooth fly-to effect
                            },
                        },
                    },
                    () => {
                        this.state.map?.setProps({ viewState: this.state.mapProps?.viewState });
                    }
                );
            });
    };

    renderOutline = async (): Promise<void> => {
        const { mapProps } = this.state;
        let layers = mapProps?.layers || [];

        const getFillColor = (key: string, color?: string) => {
            const defaultColor = this.props.port.meta_data.defaultColours?.[key]?.default;

            if (color) {
                return { colour: color, opacity: 0.5 };
            }

            return { colour: defaultColor, opacity: 0.5 };
        };

        const getStrokeColor = (key: string, color?: string) => {
            const defaultColor = this.props.port.meta_data.defaultColours?.[key]?.default;

            if (color) {
                return { colour: color, opacity: 1, width: 2 };
            }

            return { colour: defaultColor, opacity: 1, width: 2 };
        };

        const moveToolTipsOrders: any[] = [];

        layers = this.replaceLayers(layers, [
            new GeoJsonLayer<GeoJSONFeature>({
                id: this.props.layers.playbackVesselOutline.uuid,
                //@ts-ignore
                data: this.state.outlineFeatures
                    .map((outlineFeature, index) => {
                        const { data, rawData, type } = findBestData(outlineFeature.data, null, this.state.currentTime);

                        if (!rawData?.data || !data) {
                            return false;
                        }

                        // const position = data
                        //     .filter(
                        //         //@ts-ignore
                        //         (item) => item.valid_time_utc.toTimestamp() <= this.state.currentTime?.toTimestamp()
                        //     )
                        //     .sort((a, b) => b.valid_time_utc.toTimestamp() - a.valid_time_utc.toTimestamp())[0];

                        const { position, center } = findBestPosition(
                            data,
                            type,
                            rawData?.data,
                            //@ts-ignore
                            this.state.currentTime
                        );

                        if (!position) {
                            return false;
                        }

                        moveToolTipsOrders.push(
                            ...[
                                this.props.layers.playbackVesselOutline.name,
                                this.props.layers.vesselTrackHighlightPoints.name,
                                this.props.layers.vesselTrack.name,
                            ].map((layer) => ({
                                position,
                                layer,
                                parent: outlineFeature.uuid,
                            }))
                        );

                        const { outline, color } = getVesselDisplayFeature(
                            position,
                            outlineFeature.vessel,
                            outlineFeature.data.offsets[position.vessel_offset_id],
                            outlineFeature.vessel.vessel_category,
                            this.props.vesselCategories,
                            this.props.port.id,
                            //@ts-ignore
                            this.state.mapProps?.viewState.zoom,
                            //@ts-ignore
                            parseFloat(this.props.port.latitude),

                            center
                        );

                        return updateFeatureProperties(
                            `vesselOutLine-${outlineFeature.vessel_id}-${index}`,
                            this.geometry.transformerGEOJSON({
                                type: 'Feature',
                                geometry: { type: 'Polygon', coordinates: [outline] },
                            }),
                            {
                                index: index,
                                time: position.valid_time_utc,
                                dataIndex: {
                                    index: position.id,
                                    navigational_status_id: position.navigational_status_id,
                                    time: position.valid_time_utc,
                                    dataType: type,
                                },
                                drawIndex: this.props.layers.playbackVesselOutline.drawIndex,
                                clickIndex: this.props.layers.playbackVesselOutline.clickIndex,
                                fillColorUpdate: () => getFillColor('vesselOutlineMonoColour', color),
                                strokeColorUpdate: () => getStrokeColor('vesselOutlineMonoColour', color),
                                fill: getFillColor('vesselOutlineMonoColour', color),
                                stroke: getStrokeColor('vesselOutlineMonoColour', color),
                                name: outlineFeature.vessel.name,
                                display: true,
                                visible: true,
                                //displayUpdate: () => getDisplayStatus(transit.id, 'outline', 'vesselOutline'),
                                layer: this.props.layers.playbackVesselOutline.name,
                                layerGroub: this.props.layers.playbackVesselOutline.group,
                                parent: outlineFeature.uuid,
                                description: null,
                            },
                            this.props.layers.playbackVesselOutline.name
                        );
                    })
                    .filter((f): f is GeoJSONFeature => f !== null),
                //@ts-ignore
                display: true,
                visible: true,
                index: this.props.layers.playbackVesselOutline.drawIndex,
                //displayUpdate: () => getDisplayStatus(transit.id, 'drawTrack', 'vesselTracks'),
                layer: this.props.layers.playbackVesselOutline.name,
                layerGroup: this.props.layers.playbackVesselOutline.group,
                pickable: true,
                stroked: true, // Enable stroked borders for the polygons
                filled: true, // Enable filling for the polygons
                extruded: false,
                pointRadiusScale: 10,
                lineWidthUnits: 'pixels',
                getFillColor: (feature): RGBAColor => {
                    return feature.properties?.visible
                        ? //@ts-ignore
                          this.state.currentTime.getDiffInSeconds(feature.properties?.dataIndex.time) <
                          (feature.properties?.dataIndex.navigational_status_id === 5 ||
                          feature.properties?.dataIndex.navigational_status_id === 1
                              ? //@ts-ignore
                                this.state.analysisVariables?.aisDataDropoutMoored_seconds
                              : //@ts-ignore
                                this.state.analysisVariables?.aisDataDropoutUnderway_seconds)
                            ? [...hexToRgb(feature.properties?.fill.colour), feature.properties?.fill.opacity * 255]
                            : [
                                  ...hexToRgb(
                                      this.props.port.meta_data.defaultColours?.['vesselOutlineExpiredData']?.default
                                  ),
                                  feature.properties?.fill.opacity * 255,
                              ]
                        : [0, 0, 0, 0]; // Transparent if not visible
                },
                getLineColor: (feature): RGBAColor => {
                    return feature.properties?.visible
                        ? //@ts-ignore
                          this.state.currentTime.getDiffInSeconds(feature.properties?.dataIndex.time) <
                          (feature.properties?.dataIndex.navigational_status_id === 5 ||
                          feature.properties?.dataIndex.navigational_status_id === 1
                              ? //@ts-ignore
                                this.state.analysisVariables?.aisDataDropoutMoored_seconds
                              : //@ts-ignore
                                this.state.analysisVariables?.aisDataDropoutUnderway_seconds)
                            ? [...hexToRgb(feature.properties?.stroke.colour), feature.properties?.stroke.opacity * 255]
                            : [
                                  ...hexToRgb(
                                      this.props.port.meta_data.defaultColours?.['vesselOutlineExpiredData']?.default
                                  ),
                                  feature.properties?.stroke.opacity * 255,
                              ]
                        : [0, 0, 0, 0]; // Transparent if not visible
                },
                getLineWidth: (feature) => (feature.properties?.visible ? feature.properties?.stroke.width || 1 : 0), // 0 width hides the stroke

                getLineOpacity: (feature) => {
                    return feature.properties?.stroke ? feature.properties?.stroke.opacity || 1 : 1; // Default opacity: 1 if not provided
                },
            }) as unknown as DeckGLLayer,

            new GeoJsonLayer<GeoJSONFeature>({
                id: this.props.layers.vesselTrackHighlightPoints.uuid,
                //@ts-ignore
                data: this.state.trackFeatures.flatMap((trackFeature, index) => {
                    // Function to process a single type of data (e.g., rawAIS, centroid, smoothed)
                    const createPoint = (dataType: 'rawAIS' | 'centroid' | 'smoothed') => {
                        const position = trackFeature.data[dataType]
                            ?.slice() // copy array
                            .reverse()
                            //@ts-ignore
                            .find((item) => item.valid_time_utc.toTimestamp() <= this.state.currentTime?.toTimestamp());

                        if (!position) {
                            return null; // Skip if no valid position is found
                        }

                        // Return a GeoJSON feature for the current data type
                        return updateFeatureProperties(
                            `trackFeaturePoint-${trackFeature.vessel_id}-${index}-${dataType}`,
                            this.geometry.transformerGEOJSON({
                                type: 'Feature',
                                geometry: { type: 'Point', coordinates: [position.east_utm, position.north_utm] },
                            }),
                            {
                                index: index,
                                time: position.valid_time_utc,
                                dataIndex: {
                                    index: position.id,
                                    navigational_status_id: position.navigational_status_id,
                                    time: position.valid_time_utc,
                                    dataType,
                                },
                                drawIndex: this.props.layers.vesselTrackHighlightPoints.drawIndex,
                                clickIndex: this.props.layers.vesselTrackHighlightPoints.clickIndex,
                                fillColorUpdate: () => getFillColor(`${dataType}Track`),
                                strokeColorUpdate: () => getStrokeColor(`${dataType}Track`),
                                fill: getFillColor(`${dataType}Track`),
                                stroke: getStrokeColor(`${dataType}Track`),
                                name: trackFeature.vessel.name,
                                display: true,
                                visible: true,
                                layer: this.props.layers.vesselTrackHighlightPoints.name,
                                layerGroub: this.props.layers.vesselTrackHighlightPoints.group,
                                parent: trackFeature.uuid,
                                description: `${dataType} data`,
                            },
                            this.props.layers.vesselTrackHighlightPoints.name
                        );
                    };

                    // Create points for rawAIS, centroid, and smoothed
                    return ['rawAIS', 'centroid', 'smoothed'].map(createPoint).filter(Boolean); // Remove null values
                }),

                // Other properties
                //@ts-ignore
                display: true,
                visible: true,
                index: this.props.layers.vesselTrackHighlightPoints.drawIndex,
                layer: this.props.layers.vesselTrackHighlightPoints.name,
                layerGroup: this.props.layers.vesselTrackHighlightPoints.group,
                pickable: true,
                stroked: true, // Enable stroked borders for the polygons
                filled: true, // Enable filling for the polygons
                extruded: false,
                lineWidthUnits: 'pixels',
                pointRadiusScale: 2,
                getFillColor: (feature): RGBAColor => {
                    return feature.properties?.visible
                        ? [...hexToRgb(feature.properties?.fill.colour), feature.properties?.fill.opacity * 255]
                        : [0, 0, 0, 0]; // Transparent if not visible
                },
                getLineColor: (feature): RGBAColor => {
                    return feature.properties?.visible
                        ? [...hexToRgb(feature.properties?.stroke.colour), feature.properties?.stroke.opacity * 255]
                        : [0, 0, 0, 0]; // Transparent if not visible
                },
                getLineWidth: (feature) => (feature.properties?.visible ? feature.properties?.stroke.width || 1 : 0), // 0 width hides the stroke
                getLineOpacity: (feature) => {
                    return feature.properties?.stroke ? feature.properties?.stroke.opacity || 1 : 1; // Default opacity: 1 if not provided
                },
            }) as unknown as DeckGLLayer,
        ]);
        this.moveTooltipsWithTime(moveToolTipsOrders);
        await this.renderMap({ layers: layers });
    };

    renderPoints = async (): Promise<void> => {
        const { mapProps } = this.state;
        let layers = mapProps?.layers || [];

        const getFillColor = (key: string, color?: string) => {
            const defaultColor = this.props.port.meta_data.defaultColours?.[key]?.default;

            if (color) {
                return { colour: color, opacity: 0.5 };
            }

            return { colour: defaultColor, opacity: 0.5 };
        };

        const getStrokeColor = (key: string, color?: string) => {
            const defaultColor = this.props.port.meta_data.defaultColours?.[key]?.default;

            if (color) {
                return { colour: color, opacity: 1, width: 2 };
            }

            return { colour: defaultColor, opacity: 1, width: 2 };
        };

        const transitVessel = this.state.outlineFeatures.find((x: TrackType) => {
            return x.options.group === 'transitVessels';
        });

        const createPoints = (dataType: 'rawAIS' | 'centroid' | 'smoothed') => {
            if (!this.state.switchs[dataType].value || !transitVessel || !transitVessel.options.display) {
                return [];
            }
            return transitVessel.data[dataType].map((position, index) => {
                // Return a GeoJSON feature for the current data type
                return updateFeatureProperties(
                    `trackFeaturePoint-${transitVessel.vessel_id}-${index}-${dataType}`,
                    this.geometry.transformerGEOJSON({
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [position.east_utm, position.north_utm],
                        },
                    }),
                    {
                        index: index,
                        time: position.valid_time_utc,
                        dataIndex: {
                            index: position.id,
                            navigational_status_id: position.navigational_status_id,
                            time: position.valid_time_utc,
                            dataType,
                        },
                        drawIndex: this.props.layers.vesselTrackPoints.drawIndex,
                        clickIndex: this.props.layers.vesselTrackPoints.clickIndex,
                        fillColorUpdate: () => getFillColor(`${dataType}Track`),
                        strokeColorUpdate: () => getStrokeColor(`${dataType}Track`),
                        fill: getFillColor(`${dataType}Track`),
                        stroke: getStrokeColor(`${dataType}Track`),
                        name: transitVessel.vessel.name,
                        display: true,
                        visible: true,
                        layer: this.props.layers.vesselTrackPoints.name,
                        layerGroup: this.props.layers.vesselTrackPoints.group,
                        parent: transitVessel.uuid,
                        description: `${dataType} data`,
                    },
                    this.props.layers.vesselTrackPoints.name
                );
            });
        };

        layers = this.replaceLayers(layers, [
            new GeoJsonLayer<GeoJSONFeature>({
                id: this.props.layers.vesselTrackPoints.uuid,
                data: ['rawAIS', 'centroid', 'smoothed'].map(createPoints).filter(Boolean).flat(),
                // Other properties
                //@ts-ignore
                display: true,
                visible: true,
                index: this.props.layers.vesselTrackPoints.drawIndex,
                layer: this.props.layers.vesselTrackPoints.name,
                layerGroup: this.props.layers.vesselTrackPoints.group,
                pickable: true,
                stroked: true, // Enable stroked borders for the polygons
                filled: true, // Enable filling for the polygons
                extruded: false,
                lineWidthUnits: 'pixels',
                pointRadiusScale: 1,
                getFillColor: (feature): RGBAColor => {
                    return feature.properties?.visible
                        ? [...hexToRgb(feature.properties?.fill.colour), feature.properties?.fill.opacity * 255]
                        : [0, 0, 0, 0]; // Transparent if not visible
                },
                getLineColor: (feature): RGBAColor => {
                    return feature.properties?.visible
                        ? [...hexToRgb(feature.properties?.stroke.colour), feature.properties?.stroke.opacity * 255]
                        : [0, 0, 0, 0]; // Transparent if not visible
                },
                getLineWidth: (feature) => (feature.properties?.visible ? feature.properties?.stroke.width || 1 : 0), // 0 width hides the stroke
                getLineOpacity: (feature) => {
                    return feature.properties?.stroke ? feature.properties?.stroke.opacity || 1 : 1; // Default opacity: 1 if not provided
                },
            }) as unknown as DeckGLLayer,
        ]);

        await this.renderMap({ layers: layers });
    };

    renderEvents = async (): Promise<void> => {
        const { mapProps } = this.state;
        let layers = mapProps?.layers || [];

        const transitVessel = this.state.outlineFeatures.find((x: TrackType) => {
            return x.options.group === 'transitVessels';
        });
        if (!transitVessel) {
            await this.renderMap({ layers: layers });
        }

        console.log(transitVessel);
        const createPoints = (): (GeoJSONFeature | null)[][] => {
            const events = {
                debugTags: [
                    'Manoeuvre',
                    'data',
                    'startGapPoint',
                    'endGapPoint',
                    'enterRegion',
                    'exitRegion',
                    'sweptPathAreaIntersection',
                ],
                routeTags: [
                    'enterSwing',
                    'exitSwing',
                    'enterChannel',
                    'exitChannel',
                    'enterPilotBoardingGround',
                    'exitPilotBoardingGround',
                    'enterAnchorage',
                    'exitAnchorage',
                    'enterBerth',
                    'atBerth',
                    'exitBerth',
                ],
                vesselStatus: ['vesselStatus'],
                systemTags: ['endTransit', 'startTransit'],
            };
            //@ts-ignore
            return Object.keys(events).map((event) => {
                if (!this.state.switchs[event].value) {
                    return [];
                }

                if (!transitVessel) {
                    return [];
                }

                return transitVessel.data.events
                    .filter((x) => {
                        return events[event].includes(x.type);
                    })
                    .map((x, index) => {
                        const { center, data } = findBestData(transitVessel.data);
                        const position = data.find((item, index, array) => {
                            const isValid =
                                //@ts-ignore
                                item.valid_time_utc.toTimestamp() >=
                                this.props.dateTimeFormat.parseDateTime(x.valid_time_utc).toTimestamp();
                            const isLast = item.id === array[array.length - 1].id;

                            return isValid && !isLast; // Return true for the first valid point that is not the last
                        });

                        if (!position) {
                            return false;
                        }

                        return updateFeatureProperties(
                            `trackFeaturePoint-${transitVessel.vessel_id}-${index}-event`,
                            this.geometry.transformerGEOJSON({
                                type: 'Feature',
                                geometry: { type: 'Point', coordinates: [position.east_utm, position.north_utm] },
                            }),
                            {
                                index: index,
                                time: position.valid_time_utc,
                                dataIndex: {
                                    index: position.id,
                                    time: position.valid_time_utc,
                                    navigational_status_id: position.navigational_status_id,
                                    group: event,
                                    event: x.type,
                                    description: x.description,
                                },
                                drawIndex: this.props.layers.transitTrackEvents.drawIndex,
                                clickIndex: this.props.layers.transitTrackEvents.clickIndex,
                                fillColorUpdate: () => {
                                    return { colour: TRACK_EVENT_COLOR, opacity: 1 };
                                },
                                strokeColorUpdate: () => {
                                    return { colour: TRACK_EVENT_COLOR, opacity: 1 };
                                },
                                fill: { colour: TRACK_EVENT_COLOR, opacity: 1 },
                                stroke: { colour: TRACK_EVENT_COLOR, opacity: 1 },
                                name: transitVessel.vessel.name,
                                display: true,
                                visible: true,
                                layer: this.props.layers.transitTrackEvents.name,
                                layerGroup: this.props.layers.transitTrackEvents.group,
                                parent: transitVessel.uuid,
                                description: `Event data`,
                            },
                            this.props.layers.transitTrackEvents.name
                        );
                    });
            });
        };

        layers = this.replaceLayers(layers, [
            new GeoJsonLayer<GeoJSONFeature>({
                id: this.props.layers.transitTrackEvents.uuid,
                //@ts-ignore
                data: createPoints().flat(),
                // Other properties
                //@ts-ignore
                display: true,
                visible: true,
                index: this.props.layers.transitTrackEvents.drawIndex,
                layer: this.props.layers.transitTrackEvents.name,
                layerGroup: this.props.layers.transitTrackEvents.group,
                pickable: true,
                stroked: true, // Enable stroked borders for the polygons
                filled: true, // Enable filling for the polygons
                extruded: false,
                lineWidthUnits: 'pixels',
                pointRadiusScale: 3,
                getFillColor: (feature): RGBAColor => {
                    return feature.properties?.visible
                        ? [...hexToRgb(feature.properties?.fill.colour), feature.properties?.fill.opacity * 255]
                        : [0, 0, 0, 0]; // Transparent if not visible
                },
                getLineColor: (feature): RGBAColor => {
                    return feature.properties?.visible
                        ? [...hexToRgb(feature.properties?.stroke.colour), feature.properties?.stroke.opacity * 255]
                        : [0, 0, 0, 0]; // Transparent if not visible
                },
                getLineWidth: (feature) => (feature.properties?.visible ? feature.properties?.stroke.width || 1 : 0), // 0 width hides the stroke
                getLineOpacity: (feature) => {
                    return feature.properties?.stroke ? feature.properties?.stroke.opacity || 1 : 1; // Default opacity: 1 if not provided
                },
            }) as unknown as DeckGLLayer,
        ]);

        await this.renderMap({ layers: layers });
    };

    renderTracks = async (): Promise<void> => {
        const { mapProps } = this.state;
        let layers = mapProps?.layers || [];

        const getFillColor = (key: string, color?: string) => {
            const defaultColor = this.props.port.meta_data.defaultColours?.[key]?.default;

            if (color) {
                return { colour: color, opacity: 0.5 };
            }

            return { colour: defaultColor, opacity: 0.5 };
        };

        const getStrokeColor = (key: string, color?: string) => {
            const defaultColor = this.props.port.meta_data.defaultColours?.[key]?.default;

            if (color) {
                return { colour: color, opacity: 1, width: 2 };
            }

            return { colour: defaultColor, opacity: 1, width: 2 };
        };

        layers = this.replaceLayers(layers, [
            new GeoJsonLayer<GeoJSONFeature>({
                id: this.props.layers.vesselTrack.uuid,
                //@ts-ignore
                data: this.state.trackFeatures
                    .filter((trackFeature) => {
                        return trackFeature.options.display;
                    })
                    .map((trackFeature, index) => {
                        const { data, type } = findBestData(
                            trackFeature.data,
                            this.state.startTime,
                            this.state.endTime
                        );

                        return updateFeatureProperties(
                            `trackFeature-${trackFeature.vessel_id}-${index}`,
                            this.geometry.transformerGEOJSON({
                                type: 'Feature',
                                geometry: {
                                    type: 'LineString',
                                    coordinates: data.map((x) => [x.east_utm, x.north_utm]),
                                },
                            }),
                            {
                                index: index,
                                drawIndex: this.props.layers.vesselTrack.drawIndex,
                                clickIndex: this.props.layers.vesselTrack.clickIndex,
                                fillColorUpdate: () => getFillColor(`${type}Track`),
                                strokeColorUpdate: () => getStrokeColor(`${type}Track`),
                                fill: getFillColor(`${type}Track`),
                                stroke: getStrokeColor(`${type}Track`),
                                name: trackFeature.vessel.name,
                                display: true,
                                visible: true,
                                layer: this.props.layers.vesselTrack.name,
                                layerGroup: this.props.layers.vesselTrack.group,
                                parent: trackFeature.uuid,
                                description: null,
                            },
                            this.props.layers.vesselTrack.name
                        );
                    })
                    .filter((f): f is GeoJSONFeature => f !== null),
                //@ts-ignore
                display: true,
                visible: true,
                index: this.props.layers.vesselTrack.drawIndex,
                layer: this.props.layers.playbackVesselOutline.name,
                layerGroup: this.props.layers.playbackVesselOutline.group,
                pickable: true,
                stroked: true,
                filled: true,
                lineWidthUnits: 'pixels',
                extruded: false,
                pointRadiusScale: 2,
                getFillColor: (feature): RGBAColor => {
                    return feature.properties?.visible
                        ? [...hexToRgb(feature.properties?.fill.colour), feature.properties?.fill.opacity * 255]
                        : [0, 0, 0, 0]; // Transparent if not visible
                },
                getLineColor: (feature): RGBAColor => {
                    return feature.properties?.visible
                        ? [...hexToRgb(feature.properties?.stroke.colour), feature.properties?.stroke.opacity * 255]
                        : [0, 0, 0, 0]; // Transparent if not visible
                },
                getLineWidth: (feature) => (feature.properties?.visible ? feature.properties?.stroke.width || 1 : 0), // 0 width hides the stroke

                getLineOpacity: (feature) => {
                    return feature.properties?.stroke ? feature.properties?.stroke.opacity || 1 : 1; // Default opacity: 1 if not provided
                },
            }) as unknown as DeckGLLayer,
        ]);

        await this.renderMap({ layers: layers });
    };

    /**
     * Creates the map with background layers and a GeoJson layer.
     *
     * @returns {Promise<void>} A promise indicating the completion of map creation.
     */
    createMap = async () => {
        const layers = [
            await getLayerProvider(
                this.state.coordinate,
                this.state.backgrounds.find((x) => {
                    return x.status === 'active' && x.type === 'basemap';
                }),
                0
            ),
            await getLayerProvider(
                this.state.coordinate,
                this.state.backgrounds.find((x) => {
                    return x.status === 'active' && x.type === 'overlay';
                }),
                1
            ),
        ].filter((item) => !!item);

        const mapProps = {
            canvas: this.state.uuid,
            viewState: {
                latitude: this.props.layout.viewPort?.latitude || this.state.coordinate?.[0] || 0,
                longitude: this.props.layout.viewPort?.longitude || this.state.coordinate?.[1] || 0,
                zoom: this.props.layout.viewPort?.zoom || this.state.zoom - 1,
                minZoom: 10, // Set the minimum zoom level
                maxZoom: 18, // Set the maximum zoom level
                pitch: 0,
                bearing: 0,
                altitude: 1,
                maxPitch: 60,
                minPitch: 0,
                normalize: true,
                position: [0, 0, 0],
                width: this.canvasRef?.current?.clientWidth,
                height: this.canvasRef?.current?.clientHeight,
            },
            initialViewState: {
                latitude: this.props.layout.viewPort?.latitude || this.state.coordinate?.[0] || 0,
                longitude: this.props.layout.viewPort?.longitude || this.state.coordinate?.[1] || 0,
                zoom: this.props.layout.viewPort?.zoom || this.state.zoom - 1,
                minZoom: 10, // Set the minimum zoom level
                maxZoom: 18, // Set the maximum zoom level
                pitch: 0,
                bearing: 0,
                altitude: 1,
                maxPitch: 60,
                minPitch: 0,
                normalize: true,
                position: [0, 0, 0],
                width: this.canvasRef?.current?.clientWidth,
                height: this.canvasRef?.current?.clientHeight,
            },
            tooltip: false,
            controller: {
                doubleClickZoom: true,
            },
            getCursor: ({ isDragging }) => {
                return isDragging ? 'grabbing !important' : 'default';
            },
            onHover: this.handleOnHover,
            onDragEnd: this.handleOnDragEnd,
            onDragStart: this.handleOnDragStart,
            onDrag: this.handleOnDrag,
            onClick: this.handleOnClick,
            onViewStateChange: this.handleViewStateChange,
            onAfterRender: this.handleAfterRender,

            layers: layers,
        };

        window?.log('EVENT : Playback map created', 'color: orange');
        const map = new DeckGl(mapProps);
        //@ts-ignore
        this.setState({ map, mapProps: { ...mapProps, canvas: map.getCanvas() } }, async () => {
            this.props.setMapAction({ playback: map });
            this.props.setMapControlsAction({
                playback: {
                    hideTooltips: this.hideTooltips,
                    clearTooltips: this.clearTooltips,
                    getDrawingCount: this.getDrawingCount,
                    hideTooltipById: this.hideTooltipById,
                    flagTooltipId: this.flagTooltipId,
                    removeDrawingById: this.removeDrawingById,
                    removeDrawingToolTipById: this.removeDrawingToolTipById,
                    moveTooltipWithTime: this.moveTooltipWithTime,
                    refreshFeatures: this.refreshFeatures,
                    clearMap: this.clearMap,
                    updateTracks: this.updateTracks,
                    portAreaFeatureVisiblity: this.portAreaFeatureVisiblity,
                    goToVesselLocation: this.goToVesselLocation,
                    setDrawingMode: this.setDrawingMode,
                    getDrawingMode: this.getDrawingMode,
                    getDrawingFeatures: this.getDrawingFeatures,
                    zoomIn: this.zoomIn,
                    zoomOut: this.zoomOut,
                    getCurrentTime: this.getCurrentTime,
                    closePlaybackMode: this.closePlaybackMode,
                    jumpToTime: this.jumpToTime,
                    setViewPort: this.setViewPort,
                },
            });

            this.createPortAreas(this.state.pilotagePlans.length >= 1);
        });
    };

    getCurrentTime = () => {
        return this.state.currentTime;
    };

    //unused function
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    afterClick = (id: number, time: DateTimeFormat | null) => {};

    changeSpeed = (speed: number) => {
        this.setState({ speed });
    };

    jumpToTime: JumpToTimeInterface = (selectedTime) => {
        this.setState(
            {
                currentTime: selectedTime,
            },
            this.renderOutline
        );
    };

    changeTime = (seconds: number) => {
        this.setState((prevState) => {
            const newTime = prevState.currentTime?.clone().plus({ second: seconds });

            if (!newTime) {
                return null;
            } // Safeguard in case `currentTime` is undefined

            if (newTime.isAfter(prevState.endTime?.date)) {
                return { currentTime: prevState?.endTime?.clone() };
            }

            if (newTime.isBefore(prevState.startTime?.date)) {
                return { currentTime: prevState?.startTime?.clone() };
            }
            this.props.menuControls.playback?.getMetOceanByTime(newTime);
            return { currentTime: newTime };
        }, this.renderOutline);
    };

    render() {
        return (
            <div
                className={twclsx(
                    !this.state.active && 'hidden',
                    'h-page',
                    'padding-transition',
                    'fixed',
                    'z-10',
                    'left-0',
                    'right-0',
                    'margin-transition',
                    'ml-14',
                    'w-fullPage',
                    'mt-14'
                )}
            >
                <canvas
                    id={this.state.uuid}
                    ref={this.canvasRef}
                    className={twclsx(
                        'fixed',
                        this.state.drawingMode !== 'pointer' && 'mouse-crosshair',
                        'z-10',
                        'w-screen',
                        'h-screen',
                        'left-0',

                        'right-0'
                    )}
                ></canvas>
                <ToolTip
                    mode="playback"
                    enviromental={this.state.switchs?.playbackEnviromentalDataOnRightColumn?.value}
                    manoeuvre={this.state.switchs?.playbackManoeuvreInformationOnRightColumn?.value}
                    updateTooltips={this.updateTooltips}
                    updateDrawinglabel={this.updateDrawinglabel}
                    currentTime={this.state.currentTime}
                    tooltips={this.state.tooltips}
                ></ToolTip>
                <StaticToolTip
                    mode="playback"
                    enviromental={this.state.switchs?.playbackEnviromentalDataOnRightColumn?.value}
                    manoeuvre={this.state.switchs?.playbackManoeuvreInformationOnRightColumn?.value}
                    updateTooltips={this.updateTooltips}
                    currentTime={this.state.currentTime}
                    tooltips={this.state.tooltips}
                ></StaticToolTip>
                <ChangeLayer
                    save={true}
                    backgrounds={this.state.backgrounds}
                    changeBackground={this.changeBackground}
                    changeOverlay={this.changeOverlay}
                    move={true}
                ></ChangeLayer>
                <MeasurePosition
                    hoverMeasureData={this.state.hoverMeasureData}
                    coordinate={this.state?.coordinate}
                ></MeasurePosition>
                <MousePosition backgrounds={this.state.backgrounds} coordinate={this.state?.coordinate}></MousePosition>
                <Attribution backgrounds={this.state.backgrounds}></Attribution>
                <Controls parent="playback" move={true}></Controls>
                <TimeControl
                    backgrounds={this.state.backgrounds}
                    speed={this.state.speed}
                    changeSpeed={this.changeSpeed}
                    changeTime={this.changeTime}
                    startTimer={this.startTimer}
                    stopTimer={this.stopTimer}
                    currentTime={this.state.currentTime}
                    startTime={this.state.startTime}
                    endTime={this.state.endTime}
                    play={this.state.play}
                ></TimeControl>
            </div>
        );
    }
}

const playbackStateToProps = (state: RootState) => ({
    layout: state.layout.layout,
    vesselCategories: state.port.vesselCategories,
    port: state.port.port,
    layers: state.layout.layers,
    outlineInterval: state.map.outlineInterval,
    switchs: state.layout.switchs,
    pilotagePlanSettings: state.layout.pilotagePlanSettings,
    pilotagePlans: state.port.pilotagePlans,
    mainControls: state.layout.mainControls,
    dateTimeFormat: state.port.dateTimeFormat,
    menuControls: state.layout.menuControls,
    openModelFunction: state.layout.openModelFunction,
});
const playbackDispatchToProps = (dispatch: Dispatch) => ({
    setMapAction: bindActionCreators(setMapAction, dispatch),
    setMapControlsAction: bindActionCreators(setMapControlsAction, dispatch),
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
    setSystemLoadingAction: bindActionCreators(setSystemLoadingAction, dispatch),
    showMessageAction: bindActionCreators(showMessageAction, dispatch),
    setLayoutAction: bindActionCreators(setLayoutAction, dispatch),
});

export default connect(playbackStateToProps, playbackDispatchToProps)(Playback);
